<template>
                <div v-if="currentView === 'invoice_drive'">
                    <div class="flex items-center justify-between mb-4">
                        <div class="page-header"><div><div class="page-title">發票專區</div><div class="page-subtitle">上傳並解析電子發票與管理紀錄</div></div></div>
                        <div class="flex gap-2 items-center flex-wrap">
                            <button type="button" class="btn btn-secondary" @click="invoiceDriveFetchFiles" :disabled="invoiceDriveState?.loading">重新整理</button>
                            <button type="button" :class="invoiceDriveState?.batchMode ? 'btn btn-warning btn-sm' : 'btn btn-secondary btn-sm'" @click="invoiceDriveBatchToggleMode">{{ invoiceDriveState?.batchMode ? '退出批次' : '批次模式' }}</button>
                            <button type="button" class="btn btn-primary btn-sm" @click="invoiceDriveSeedExamples" :disabled="invoiceDriveState?.seedLoading">建立範例</button>
                            <button type="button" class="btn btn-success btn-sm" @click="invoiceDriveExportCsv" :disabled="invoiceDriveState?.exporting">匯出 CSV</button>
                        </div>
                    </div>

                    <!-- Statistics Summary -->
                    <div v-if="invoiceDriveState?.files?.length" class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                        <div class="card p-3 text-center">
                            <div class="text-2xl font-bold" style="color:var(--primary);">{{ invoiceDriveState.files.length }}</div>
                            <div class="text-xs mt-1" style="color:var(--gray-500);">總筆數</div>
                        </div>
                        <div class="card p-3 text-center">
                            <div class="text-2xl font-bold" style="color:var(--success);">{{ invoiceDriveState.files.filter(f => f.status === 'confirmed').length }}</div>
                            <div class="text-xs mt-1" style="color:var(--gray-500);">已確認</div>
                        </div>
                        <div class="card p-3 text-center">
                            <div class="text-2xl font-bold" style="color:var(--warning);">{{ invoiceDriveState.files.filter(f => f.status === 'needs_review').length }}</div>
                            <div class="text-xs mt-1" style="color:var(--gray-500);">待審核</div>
                        </div>
                        <div class="card p-3 text-center">
                            <div class="text-2xl font-bold" style="color:var(--danger);">{{ invoiceDriveState.files.filter(f => f.status === 'failed').length }}</div>
                            <div class="text-xs mt-1" style="color:var(--gray-500);">解析失敗</div>
                        </div>
                        <div class="card p-3 text-center">
                            <div class="text-2xl font-bold" style="color:var(--gray-800);">{{ invoiceDriveState.files.filter(f => f.total != null).reduce((s, f) => s + (Number(f.total) || 0), 0).toLocaleString() }}</div>
                            <div class="text-xs mt-1" style="color:var(--gray-500);">總金額</div>
                        </div>
                    </div>

                    <div class="card p-4 mb-6">
                        <div class="flex flex-col md:flex-row md:items-end gap-3">
                            <div class="flex-1">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">上傳發票（圖片 / PDF）</label>
                                <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.pdf,image/*,application/pdf" @change="invoiceDriveOnFilesSelected" class="input p-2" />
                                <div v-if="invoiceDriveState?.selectedFiles?.length" class="mt-2 text-xs" style="color:var(--gray-600);">
                                    已選擇 {{ invoiceDriveState.selectedFiles.length }} 個檔案
                                </div>
                            </div>
                            <div class="flex gap-2">
                                <button type="button" class="btn btn-primary" @click="invoiceDriveUploadSelected" :disabled="invoiceDriveState?.uploading || !(invoiceDriveState?.selectedFiles?.length)">上傳並解析</button>
                                <button type="button" class="btn btn-secondary" @click="invoiceDriveClearSelection" :disabled="invoiceDriveState?.uploading">清除選擇</button>
                            </div>
                        </div>
                        <div v-if="invoiceDriveState?.uploadError" class="mt-3 text-sm whitespace-pre-wrap" style="color:var(--danger);">{{ invoiceDriveState.uploadError }}</div>
                        <div v-if="invoiceDriveState?.uploadResults?.length" class="mt-3 text-xs" style="color:var(--gray-700);">
                            <div class="font-semibold mb-1">上傳結果</div>
                            <div class="max-h-28 overflow-y-auto rounded p-2" style="background:var(--gray-50);border:1px solid var(--border);">
                                <div v-for="(r, idx) in invoiceDriveState.uploadResults" :key="idx" class="py-0.5">
                                    {{ r.name }} → {{ r.ok ? 'OK' : 'FAILED' }} {{ r.invoice_id ? `(invoice_id=${r.invoice_id})` : '' }} {{ r.detail ? `(${r.detail})` : '' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card p-4 mb-6">
                        <div class="flex flex-col md:flex-row md:items-end gap-3 mb-4 flex-wrap">
                            <div class="w-full md:w-44">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">狀態</label>
                                <select v-model="invoiceDriveState.filters.status" class="input p-2">
                                    <option value="">全部</option>
                                    <option value="pending">待解析</option>
                                    <option value="parsed">已解析</option>
                                    <option value="needs_review">待審核</option>
                                    <option value="confirmed">已確認</option>
                                    <option value="failed">解析失敗</option>
                                </select>
                            </div>
                            <div class="w-full md:w-36">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">發票日期起</label>
                                <input v-model="invoiceDriveState.filters.date_from" type="date" class="input p-2" />
                            </div>
                            <div class="w-full md:w-36">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">發票日期迄</label>
                                <input v-model="invoiceDriveState.filters.date_to" type="date" class="input p-2" />
                            </div>
                            <div class="flex-1">
                                <label class="block text-xs mb-1" style="color:var(--gray-600);">搜尋</label>
                                <input v-model="invoiceDriveState.filters.q" type="text" class="input p-2" placeholder="發票號碼 / 店家 / 買受人 / 統編 / 檔名" />
                            </div>
                            <div class="flex gap-2">
                                <button type="button" class="btn btn-secondary" @click="invoiceDriveFetchFiles" :disabled="invoiceDriveState?.loading">查詢</button>
                                <button type="button" class="btn btn-secondary" @click="invoiceDriveResetFilters" :disabled="invoiceDriveState?.loading">重置</button>
                            </div>
                        </div>

                        <div v-if="invoiceDriveState?.listError" class="mb-3 text-sm whitespace-pre-wrap" style="color:var(--danger);">{{ invoiceDriveState.listError }}</div>

                        <!-- Batch toolbar -->
                        <div v-if="invoiceDriveState?.batchMode" class="mb-3 flex items-center gap-3 flex-wrap p-3 rounded" style="background:var(--surface-raised,var(--gray-50));border:1px solid var(--border);">
                            <span class="text-sm font-semibold" style="color:var(--gray-800);">已選 {{ invoiceDriveState?.batchSelected?.length || 0 }} 筆</span>
                            <button type="button" class="btn btn-secondary btn-xs" @click="invoiceDriveBatchSelectAll">全選</button>
                            <button type="button" class="btn btn-secondary btn-xs" @click="invoiceDriveBatchClear">清除選取</button>
                            <button type="button" class="btn btn-success btn-sm" @click="invoiceDriveBatchConfirm" :disabled="invoiceDriveState?.batchProcessing || !invoiceDriveState?.batchSelected?.length">批次確認</button>
                            <button type="button" class="btn btn-danger btn-sm" @click="invoiceDriveBatchDelete" :disabled="invoiceDriveState?.batchProcessing || !invoiceDriveState?.batchSelected?.length">批次刪除</button>
                            <span v-if="invoiceDriveState?.batchProcessing" class="text-xs" style="color:var(--gray-500);">處理中...</span>
                        </div>

                        <div class="overflow-x-auto border rounded">
                            <table class="data-table min-w-[1100px]">
                                <thead>
                                    <tr>
                                        <th v-if="invoiceDriveState?.batchMode" class="p-2 w-10 text-center">
                                            <input type="checkbox" :checked="invoiceDriveState?.batchSelected?.length === invoiceDriveState?.files?.length && invoiceDriveState?.files?.length > 0" @change="invoiceDriveState?.batchSelected?.length === invoiceDriveState?.files?.length ? invoiceDriveBatchClear() : invoiceDriveBatchSelectAll()" />
                                        </th>
                                        <th class="text-left p-2 whitespace-nowrap w-44">時間</th>
                                        <th class="text-left p-2">檔名</th>
                                        <th class="text-left p-2 whitespace-nowrap w-32">狀態</th>
                                        <th class="text-left p-2 whitespace-nowrap w-40">發票號碼</th>
                                        <th class="text-left p-2 whitespace-nowrap w-32">發票日期</th>
                                        <th class="text-left p-2 whitespace-nowrap w-48">店家</th>
                                        <th class="text-right p-2 whitespace-nowrap w-24">金額</th>
                                        <th v-if="!invoiceDriveState?.batchMode" class="text-left p-2 sticky right-0 z-10 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.25)]" style="background:var(--gray-50);">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="invoiceDriveState?.loading">
                                        <td :colspan="invoiceDriveState?.batchMode ? 8 : 8" class="p-6 text-center" style="color:var(--gray-500);">載入中...</td>
                                    </tr>
                                    <tr v-else-if="!invoiceDriveState?.files || invoiceDriveState.files.length === 0">
                                        <td :colspan="invoiceDriveState?.batchMode ? 8 : 8" class="p-6 text-center" style="color:var(--gray-500);">尚無資料</td>
                                    </tr>
                                    <tr v-else v-for="(it, idx) in invoiceDriveState.files" :key="`${it.file_id || 'f'}_${idx}`"
                                        :style="invoiceDriveState?.batchSelected?.includes(Number(it.invoice_id)) ? 'background:var(--primary-light,rgba(59,130,246,0.08));' : ''">
                                        <td v-if="invoiceDriveState?.batchMode" class="p-2 text-center">
                                            <input type="checkbox" :checked="invoiceDriveState?.batchSelected?.includes(Number(it.invoice_id))" @change="invoiceDriveBatchToggle(it.invoice_id)" />
                                        </td>
                                        <td class="p-2 text-xs whitespace-nowrap" style="color:var(--gray-700);">{{ invoiceDriveFmtTime(it.created_at) }}</td>
                                        <td class="p-2">
                                            <div class="font-semibold" style="color:var(--gray-900);">{{ it.original_filename || '-' }}</div>
                                            <div class="text-xs" style="color:var(--gray-500);">sha256: {{ (it.sha256 || '').slice(0, 12) }}{{ (it.sha256 || '').length > 12 ? '…' : '' }}</div>
                                            <div v-if="it.page_count" class="text-xs" style="color:var(--gray-500);">頁數: {{ it.page_count }}</div>
                                        </td>
                                        <td class="p-2">
                                            <span class="inline-flex items-center px-2 py-1 rounded text-xs font-semibold"
                                                :class="invoiceDriveStatusClass(it.status)">
                                                {{ statusLabel(it.status) }}
                                            </span>
                                            <div v-if="it.last_error" class="mt-1 text-xs max-w-[18rem] truncate" :title="it.last_error" style="color:var(--danger);">{{ it.last_error }}</div>
                                        </td>
                                        <td class="p-2 max-w-[10rem] truncate" :title="invoiceDriveCompactText(it.invoice_number)">{{ invoiceDriveCompactText(it.invoice_number) }}</td>
                                        <td class="p-2 whitespace-nowrap">{{ it.invoice_date || '-' }}</td>
                                        <td class="p-2 max-w-[12rem] truncate" :title="invoiceDriveCompactText(it.vendor_name)">{{ invoiceDriveCompactText(it.vendor_name) }}</td>
                                        <td class="p-2 text-right whitespace-nowrap">{{ (it.total !== null && it.total !== undefined) ? Number(it.total).toLocaleString() : '-' }}</td>
                                        <td v-if="!invoiceDriveState?.batchMode" class="p-2 whitespace-nowrap sticky right-0 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.20)]" style="background:var(--surface);">
                                            <div class="flex items-center justify-end gap-3">
                                                <button type="button" class="hover:underline" style="color:var(--primary);" @click="invoiceDriveOpenDetail(it.invoice_id)" :disabled="invoiceDriveState?.detailLoading || !it.invoice_id">查看</button>
                                                <button type="button" class="hover:underline" style="color:var(--primary);" @click="invoiceDriveOpenDetailAndPreview(it)" :disabled="invoiceDriveState?.detailLoading || !it.invoice_id">預覽</button>
                                                <button type="button" class="hover:underline" style="color:var(--gray-700);" @click="invoiceDriveDownloadFile(it)" :disabled="invoiceDriveState?.downloadingId === it.file_id || !it.file_id">下載</button>
                                                <button type="button" class="hover:underline" style="color:var(--warning);" @click="invoiceDriveReparseFile(it.file_id)" :disabled="invoiceDriveState?.reparsingId === it.file_id || !it.file_id">重解析</button>
                                                <button type="button" class="hover:underline" style="color:var(--danger);" @click="invoiceDriveDeleteInvoice(it.invoice_id)" :disabled="invoiceDriveState?.deletingId === it.invoice_id || !it.invoice_id">刪除</button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Analytics: Monthly & Vendor breakdown -->
                    <div v-if="invoiceDriveState?.files?.length && (invoiceMonthlyReport.length || invoiceVendorReport.length)" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <!-- Monthly report -->
                        <div v-if="invoiceMonthlyReport.length" class="card p-4">
                            <div class="font-semibold mb-3" style="color:var(--gray-800);">月份統計</div>
                            <div class="overflow-x-auto">
                                <table class="data-table w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th class="text-left p-2">月份</th>
                                            <th class="text-right p-2">筆數</th>
                                            <th class="text-right p-2">已確認</th>
                                            <th class="text-right p-2">總金額</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="m in invoiceMonthlyReport" :key="m.month">
                                            <td class="p-2 font-medium" style="color:var(--gray-900);">{{ m.month }}</td>
                                            <td class="p-2 text-right" style="color:var(--gray-700);">{{ m.count }}</td>
                                            <td class="p-2 text-right" style="color:var(--success);">{{ m.confirmed }}</td>
                                            <td class="p-2 text-right font-semibold" style="color:var(--primary);">{{ m.total.toLocaleString() }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <!-- Vendor report -->
                        <div v-if="invoiceVendorReport.length" class="card p-4">
                            <div class="font-semibold mb-3" style="color:var(--gray-800);">廠商統計（前10名）</div>
                            <div class="overflow-x-auto">
                                <table class="data-table w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th class="text-left p-2">廠商名稱</th>
                                            <th class="text-right p-2">筆數</th>
                                            <th class="text-right p-2">總金額</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="v in invoiceVendorReport" :key="v.vendor">
                                            <td class="p-2 max-w-[12rem] truncate" :title="v.vendor" style="color:var(--gray-900);">{{ v.vendor }}</td>
                                            <td class="p-2 text-right" style="color:var(--gray-700);">{{ v.count }}</td>
                                            <td class="p-2 text-right font-semibold" style="color:var(--primary);">{{ v.total.toLocaleString() }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div v-if="invoiceDriveState?.detailModalOpen" class="fixed inset-0 z-50 flex items-center justify-center p-4" style="background:rgba(0,0,0,0.5);" @click.self="invoiceDriveCloseDetail">
                        <div class="w-full max-w-5xl rounded shadow max-h-[90vh] overflow-y-auto" style="background:var(--surface);">
                            <div class="flex items-center justify-between border-b p-4" style="border-color:var(--border);">
                                <div class="font-bold" style="color:var(--gray-900);">
                                    發票詳情
                                    <span v-if="invoiceDriveState?.detail?.invoice?.id" class="font-normal" style="color:var(--gray-600);">(ID: {{ invoiceDriveState.detail.invoice?.id }})</span>
                                    <span v-if="invoiceDriveHasUnsavedChanges()" class="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold" style="background:var(--danger-light,#fee2e2);color:var(--danger);">未儲存</span>
                                </div>
                                <div class="flex gap-2">
                                    <button type="button" class="btn btn-secondary" @click="invoiceDriveGotoAdjacent(-1)" :disabled="!invoiceDriveCanGotoAdjacent(-1) || invoiceDriveState?.detailLoading">上一筆</button>
                                    <button type="button" class="btn btn-secondary" @click="invoiceDriveGotoAdjacent(1)" :disabled="!invoiceDriveCanGotoAdjacent(1) || invoiceDriveState?.detailLoading">下一筆</button>
                                    <button type="button" class="btn btn-secondary" @click="invoiceDriveCloseDetail">關閉</button>
                                    <button type="button" class="btn btn-primary btn-sm" @click="invoiceDriveSaveDetail" :disabled="invoiceDriveState?.saving || invoiceDriveState?.detailLoading">儲存</button>
                                    <button type="button" class="btn btn-secondary btn-sm" @click="invoiceDriveExportPdf(invoiceDriveState.detail?.invoice?.id)" :disabled="invoiceDriveState?.pdfExporting || invoiceDriveState?.detailLoading">
                                        {{ invoiceDriveState?.pdfExporting ? '產生中...' : '匯出 PDF' }}
                                    </button>
                                    <button type="button" class="btn btn-success btn-sm" @click="invoiceDriveConfirm(invoiceDriveState.detail?.invoice?.id)" :disabled="invoiceDriveState?.confirming || invoiceDriveState?.detailLoading">確認</button>
                                    <button type="button" class="btn btn-danger btn-sm" @click="invoiceDriveDeleteInvoice(invoiceDriveState.detail?.invoice?.id)" :disabled="invoiceDriveState?.deletingId || invoiceDriveState?.detailLoading">刪除</button>
                                </div>
                            </div>

                            <div class="p-4">
                                <div v-if="invoiceDriveState?.detailError" class="mb-3 text-sm whitespace-pre-wrap" style="color:var(--danger);">{{ invoiceDriveState.detailError }}</div>

                                <div v-if="invoiceDriveState?.detailLoading" class="p-8 text-center" style="color:var(--gray-500);">載入中...</div>

                                <div v-else-if="!invoiceDriveState?.detail" class="p-8 text-center" style="color:var(--gray-500);">尚無資料</div>

                                <div v-else>
                                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">狀態</label>
                                            <select v-model="invoiceDriveState.edit.status" class="input p-2">
                                                <option value="">(空白)</option>
                                                <option value="pending">待解析</option>
                                                <option value="parsed">已解析</option>
                                                <option value="needs_review">待審核</option>
                                                <option value="confirmed">已確認</option>
                                                <option value="failed">解析失敗</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">發票號碼</label>
                                            <input v-model="invoiceDriveState.edit.invoice_number" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">日期 (YYYY-MM-DD)</label>
                                            <input v-model="invoiceDriveState.edit.invoice_date" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">店家名稱</label>
                                            <input v-model="invoiceDriveState.edit.vendor_name" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">店家統編</label>
                                            <input v-model="invoiceDriveState.edit.vendor_tax_id" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">買受人名稱</label>
                                            <input v-model="invoiceDriveState.edit.buyer_name" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">買受人統編</label>
                                            <input v-model="invoiceDriveState.edit.buyer_tax_id" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">幣別</label>
                                            <input v-model="invoiceDriveState.edit.currency" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">稅率</label>
                                            <input v-model.number="invoiceDriveState.edit.tax_rate" type="number" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">付款方式</label>
                                            <input v-model="invoiceDriveState.edit.pay_method" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">小計</label>
                                            <input v-model.number="invoiceDriveState.edit.subtotal" type="number" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">稅額</label>
                                            <input v-model.number="invoiceDriveState.edit.tax" type="number" class="input p-2" />
                                        </div>
                                        <div>
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">總計</label>
                                            <input v-model.number="invoiceDriveState.edit.total" type="number" class="input p-2" />
                                        </div>
                                        <div class="md:col-span-2">
                                            <label class="block text-xs mb-1" style="color:var(--gray-600);">備註</label>
                                            <input v-model="invoiceDriveState.edit.notes" class="input p-2" />
                                        </div>
                                    </div>

                                    <div class="mt-3">
                                        <button type="button" class="btn btn-secondary" @click="invoiceDriveRecalcTotals">
                                            依品項重新計算金額
                                        </button>
                                    </div>

                                    <div class="mt-5">
                                        <div class="flex items-center justify-between mb-2">
                                            <div class="font-semibold" style="color:var(--gray-800);">品項</div>
                                            <button type="button" class="btn btn-secondary btn-xs" @click="invoiceDriveAddItem">新增一列</button>
                                        </div>
                                        <div class="overflow-x-auto border rounded">
                                            <table class="data-table">
                                                <thead>
                                                    <tr>
                                                        <th class="text-left p-2">描述</th>
                                                        <th class="text-left p-2 w-24">數量</th>
                                                        <th class="text-left p-2 w-24">單位</th>
                                                        <th class="text-left p-2 w-28">單價</th>
                                                        <th class="text-left p-2 w-28">金額</th>
                                                        <th class="text-left p-2 w-16"></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-if="!invoiceDriveState?.edit?.items || invoiceDriveState.edit.items.length === 0">
                                                        <td colspan="6" class="p-4 text-center" style="color:var(--gray-500);">無品項</td>
                                                    </tr>
                                                    <tr v-else v-for="(row, ridx) in invoiceDriveState.edit.items" :key="`it_${ridx}`" class="border-b">
                                                        <td class="p-2">
                                                            <input v-model="row.description" class="input p-1.5" />
                                                        </td>
                                                        <td class="p-2">
                                                            <input v-model.number="row.quantity" type="number" class="input p-1.5" />
                                                        </td>
                                                        <td class="p-2">
                                                            <input v-model="row.unit" class="input p-1.5" />
                                                        </td>
                                                        <td class="p-2">
                                                            <input v-model.number="row.unit_price" type="number" class="input p-1.5" />
                                                        </td>
                                                        <td class="p-2">
                                                            <input v-model.number="row.amount" type="number" class="input p-1.5" />
                                                        </td>
                                                        <td class="p-2 text-right">
                                                            <button type="button" class="hover:underline text-xs" style="color:var(--danger);" @click="invoiceDriveRemoveItem(ridx)">刪除</button>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div class="mt-5">
                                        <div class="flex items-center justify-between mb-2">
                                            <div class="font-semibold" style="color:var(--gray-800);">檔案</div>
                                        </div>
                                        <div v-if="invoiceDriveState?.previewError" class="mb-2 text-sm whitespace-pre-wrap" style="color:var(--danger);">{{ invoiceDriveState.previewError }}</div>
                                        <div v-if="invoiceDriveState?.previewLoading" class="mb-2 text-sm" style="color:var(--gray-500);">預覽載入中...</div>
                                        <div v-else-if="invoiceDriveState?.previewUrl" class="mb-2 border rounded p-3" style="background:var(--surface);border-color:var(--border);">
                                            <div class="flex items-center gap-2">
                                                <div class="text-xs" style="color:var(--gray-600);">預覽：{{ invoiceDriveState.previewName || '-' }}</div>
                                                <button type="button" class="btn btn-secondary btn-xs ml-auto" @click="invoiceDriveClearPreview">關閉預覽</button>
                                            </div>
                                            <div v-if="(invoiceDriveState.previewMime || '').toLowerCase().includes('pdf')" class="mt-2 h-[60vh] border rounded overflow-hidden">
                                                <iframe :src="invoiceDriveState.previewUrl" class="w-full h-full"></iframe>
                                            </div>
                                            <div v-else class="mt-2 max-h-[60vh] overflow-auto rounded p-2" style="background:var(--gray-50);border:1px solid var(--border);">
                                                <img :src="invoiceDriveState.previewUrl" class="max-w-full h-auto" />
                                            </div>
                                        </div>
                                        <div v-if="!invoiceDriveState?.detail?.files || invoiceDriveState.detail.files.length === 0" class="p-4 text-center rounded" style="color:var(--gray-500);border:1px solid var(--border);">無檔案</div>
                                        <div v-else class="space-y-2">
                                            <div v-for="(f, fidx) in invoiceDriveState.detail.files" :key="`df_${f.file_id || f.id || fidx}`" class="input p-3">
                                                <div class="flex items-center gap-2">
                                                    <div class="font-semibold" style="color:var(--gray-900);">{{ f.original_filename || '-' }}</div>
                                                    <div class="ml-auto text-xs whitespace-nowrap" style="color:var(--gray-600);">
                                                        <span v-if="f.page_count">{{ f.page_count }} 頁</span>
                                                    </div>
                                                </div>
                                                <div class="text-xs" style="color:var(--gray-500);">sha256: {{ (f.sha256 || '').slice(0, 12) }}{{ (f.sha256 || '').length > 12 ? '…' : '' }}</div>
                                                <div class="mt-2 flex items-center gap-3 text-xs">
                                                    <button type="button" class="hover:underline" style="color:var(--primary);" @click="invoiceDrivePreviewFile(f)">預覽</button>
                                                    <button type="button" class="hover:underline" style="color:var(--gray-700);" @click="invoiceDriveDetailDownload(f)" :disabled="invoiceDriveState?.downloadingId === (f.file_id || f.id)">下載</button>
                                                    <button v-if="f.ocr_text" type="button" class="hover:underline" style="color:var(--info,#6366f1);" @click="invoiceDriveToggleOcr(f)">OCR</button>
                                                </div>
                                                <div v-if="invoiceDriveState?.ocrExpandedFileId === (f.file_id || f.id)" class="mt-2 border rounded p-2 max-h-64 overflow-y-auto" style="background:var(--surface);border-color:var(--border);">
                                                    <pre class="text-xs whitespace-pre-wrap" style="color:var(--gray-800);">{{ f.ocr_text }}</pre>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Domains View -->

</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useAppStore } from '../store/app.js'

const store = useAppStore()

// Spread all store state as reactive refs accessible in template
const stateRefs = storeToRefs(store)
// Destructure - Vue compiler exposes all variables declared in <script setup> to template
const {
  // Core state
  token, currentView, currentUser, loginForm, loginError,
  branding, notifications, showNotifPanel,
  // Command palette
  showCmdPalette, cmdQuery, cmdResults, cmdGrouped, cmdDefaults, cmdSelIdx,
  // Domain / Users / Departments
  domains, domainForm, showDomainForm, domainLoading,
  users, userForm, showUserForm, userLoading, userError,
  departments, deptForm, showDeptForm, deptLoading,
  // Contacts / Groups
  contacts, contactForm, showContactForm, contactLoading,
  groups, groupForm, showGroupForm, groupLoading,
  // Mail
  mailQueues, mailQueueLoading, mailQueueFilter,
  serviceStatus, serviceLoading,
  // Invoice Drive
  invoiceDriveState,
  // Quotes / Products
  quotes, quoteForm, showQuoteForm, quoteLoading,
  products, productForm, showProductForm, productLoading,
  priceBooks, priceBookForm, showPriceBookForm,
  // SSL
  sslCerts, sslLoading, sslDomain,
  // Security
  securityWizardStep, spamStats,
  securityFilterState,
  // Server settings
  serverMailSettings, serverDnsSettings, serverDbSettings, serverAiSettings,
  backupSettings,
  // Logs / Audit
  logs, logFilter, logLoading,
  mailAuditRecords, auditFilter, auditLoading,
  securityAuditLogs, securityAuditFilter,
  logAnalysisResult, logAnalysisLoading,
  // AI / RAG / Sales
  ragState, aiSalesState, aiMarketingState,
  // LINE
  lineSupportState,
  // Webmail
  webmailState, webmailAutoScanInvoices, webmailAutoScanMarkRead,
  // Unreplied
  unrepliedEmails, unrepliedLoading,
  // CBI
  cbiInsights, cbiLoading,
  // Misc
  sidebarCollapsed, sidebarMobile,
  journeyEnrollments, journeyEnrollmentsLoading,
  journeyFunnel, journeyFunnelLoading, journeyFunnelFilters,
  showJourneyEditorModal, showJourneyFunnelModal, showJourneyEnrollmentsModal,
  selectedJourneyForEnrollments, selectedJourneyForFunnel,
  journeyEditorForm,
} = stateRefs

// Methods are accessed directly from store (not reactive, no storeToRefs needed)
const {
  login, logout, switchSidebarView, canAccess,
  openCommandPalette, closeCmdPalette, updateCmdResults, execCmdItemById,
  moveCmdSel, toggleNotifPanel, markNotifRead, clearNotifs,
  fetchDomains, saveDomain, deleteDomain,
  fetchUsers, saveUser, deleteUser, resetUserPassword,
  fetchDepartments, saveDepartment, deleteDepartment,
  fetchContacts, saveContact, deleteContact, importContacts,
  fetchGroups, saveGroup, deleteGroup,
  fetchMailQueue, releaseMailQueueItem, deleteMailQueueItem, flushMailQueue,
  fetchServiceStatus, fetchSpamStats,
  fetchSslCerts, issueSslCert, deleteSslCert,
  fetchProducts, saveProduct, deleteProduct,
  fetchPriceBooks, savePriceBook, deletePriceBook,
  fetchQuotes, saveQuote, deleteQuote, sendQuote,
  fetchLogs, fetchLogAnalysis,
  fetchMailAuditRecords, fetchSecurityAuditLogs,
  fetchServerMailSettings, saveServerMailSettings,
  fetchServerDnsSettings, saveServerDnsSettings,
  fetchServerDbSettings, saveServerDbSettings,
  fetchServerAiSettings, saveServerAiSettings,
  fetchBackupSettings, saveBackupSettings, triggerBackup,
  fetchSecurityFilter, saveSecurityFilter,
  fetchRagDocuments, uploadRagDocument, deleteRagDocument, queryRag,
  fetchUnreplied,
  fetchCbiInsights,
  loadBrandingFromLocal, saveBranding,
  safeArr, keyById,
  invoiceDriveCanGotoAdjacent, invoiceDriveGotoAdjacent,
  openInvoiceDetail, closeInvoiceDetail,
  invoiceDriveFmtTime, invoiceDriveStatusClass, invoiceDriveCompactText,
  invoiceDriveConfirm, invoiceDriveDetailDownload, invoiceDriveDownloadFile,
  invoiceDriveOpenDetail, invoiceDriveOpenDetailAndPreview, invoiceDrivePreviewFile,
  invoiceDriveRemoveItem, invoiceDriveReparseFile, invoiceDriveToggleOcr,
  invoiceDriveFetchFiles, invoiceDriveSeedExamples, invoiceDriveExportCsv,
  invoiceDriveOnFilesSelected, invoiceDriveUploadSelected, invoiceDriveClearSelection,
  invoiceDriveCloseDetail, invoiceDriveSaveDetail, invoiceDriveRecalcTotals,
  invoiceDriveAddItem, invoiceDriveClearPreview, invoiceDriveResetFilters,
  invoiceDriveHasUnsavedChanges, invoiceDriveDeleteInvoice, invoiceDriveExportPdf,
  invoiceDriveBatchToggle, invoiceDriveBatchSelectAll, invoiceDriveBatchClear,
  invoiceDriveBatchToggleMode, invoiceDriveBatchConfirm, invoiceDriveBatchDelete,
} = store

// Chinese status labels
const statusLabel = (s) => {
  const map = { pending: '待解析', parsed: '已解析', needs_review: '待審核', confirmed: '已確認', failed: '解析失敗' }
  return map[s] || s || '-'
}

// Monthly breakdown (grouped by invoice_date YYYY-MM)
const invoiceMonthlyReport = computed(() => {
  const files = invoiceDriveState.value?.files || []
  const months = {}
  for (const f of files) {
    if (!f.invoice_date) continue
    const month = f.invoice_date.slice(0, 7)
    if (!months[month]) months[month] = { month, count: 0, total: 0, confirmed: 0 }
    months[month].count++
    months[month].total += Number(f.total || 0)
    if (f.status === 'confirmed') months[month].confirmed++
  }
  return Object.values(months).sort((a, b) => b.month.localeCompare(a.month))
})

// Top vendors by total spend (max 10)
const invoiceVendorReport = computed(() => {
  const files = invoiceDriveState.value?.files || []
  const vendors = {}
  for (const f of files) {
    const key = f.vendor_name || '(未知廠商)'
    if (!vendors[key]) vendors[key] = { vendor: key, count: 0, total: 0 }
    vendors[key].count++
    vendors[key].total += Number(f.total || 0)
  }
  return Object.values(vendors).sort((a, b) => b.total - a.total).slice(0, 10)
})

// Auto-poll while any invoice is pending/parsing
import { onMounted, onUnmounted, watch } from 'vue'
let _pollTimer = null
function _startPoll() {
  if (_pollTimer) return
  _pollTimer = setInterval(async () => {
    const files = invoiceDriveState.value?.files || []
    const hasPending = files.some(f => f.status === 'pending')
    if (!hasPending) {
      clearInterval(_pollTimer)
      _pollTimer = null
      return
    }
    await invoiceDriveFetchFiles()
  }, 3000)
}
function _stopPoll() {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null }
}
onUnmounted(_stopPoll)

// Watch for pending invoices to start polling
watch(
  () => (invoiceDriveState.value?.files || []).some(f => f.status === 'pending'),
  (hasPending) => { if (hasPending) _startPoll() },
  { immediate: true }
)
</script>
